import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { PayPalCheckoutTokenizationOptions } from 'braintree-web/paypal-checkout';
import { connect } from 'pwa-helpers';
import { BaseComponent } from '../../../features/base-component/base-component';
import store, { waitForCart } from '../../../store/store';
import { booleanConverter } from '../../../common/converters';
import {
  PaymentDetails,
  PaymentType,
  PaypalButtonColor,
  PaypalButtonLabel,
  PaypalButtonLayout,
  PaypalButtonShape,
  RmsBookstoreAddress,
} from '../../checkout/shop-checkout.interface';
import { StoreState } from '../../../store/store.interface';
import {
  createDataCollector,
  getDataCollector,
  getLoadPaypalSdkStatus,
  getPaypalCheckoutInstance,
  loadPaypalSdk,
} from '../braintree';
import { ActionStatus } from '../../../common/constants/action-status';
import { loading } from '../../../common/constants/api-constants';
import { waitForElementsToLoad } from '../../../common/utils/delay-util';
import emit from '../../../common/events';
import { getProductsFromCart } from '../../cart/shop-cart/store/shop-cart.selectors';
import { getUserDataForUserAnalytics } from '../../user-profile/user-profile.selectors';
import { updateActiveStep } from '../../checkout/shop-checkout';
import { hasOnlyFreeTrialProducts } from '../../../common/utils/cart-util';
import { getCartAnalyticsOverrides } from '../../cart/shop-cart/shop-cart.analytics';

const CREATE_ORDER_CALLBACK_FN = 'createOrder';
const CREATE_BILLING_AGREEMENT_CALLBACK_FN = 'createBillingAgreement';

/**
 * Configure and load Paypal button.
 * Saves paypal payment details in redux.
 */
@customElement('mhecomm-paypal-button')
export default class PaypalButton extends connect(store)(BaseComponent) {
  /**
   * Flag to indicate the checkout contains shippable items.
   */
  @property({ converter: booleanConverter })
  shippable = false;

  /**
   * Braintree Client token to initialize Paypal button.
   */
  @property()
  clientToken = '';

  /**
   * Currency for Paypal transaction.
   */
  @property()
  currency = 'USD';

  /**
   * Option to set the description of the preapproved payment agreement visible to customers in their PayPal profile during Vault flows. Max 255 characters.
   */
  @property()
  billingAgreementDescription = '';

  /**
   * Transaction amount to be displayed in Paypal.
   */
  @property({ type: Number })
  amount = 0;

  /**
   * Value to override paypal shipping address.
   */
  @property({ attribute: false, type: Object })
  shippingAddress;

  /**
   * Allow PayPal to Capture the shipping address.
   */
  @property({ converter: booleanConverter })
  usePayPalShippingAddress = false;

  @property({ converter: booleanConverter })
  userAutoLogin = false;

  @property()
  buttonColor?: PaypalButtonColor = PaypalButtonColor.WHITE;

  @property({ type: Number })
  buttonHeightPx?: number;

  @property()
  buttonLayout?: PaypalButtonLayout = PaypalButtonLayout.VERTICAL;

  @property()
  buttonShape?: PaypalButtonShape = PaypalButtonShape.RECTANGULAR;

  @property()
  buttonLabel?: PaypalButtonLabel = PaypalButtonLabel.PAY;

  @property()
  loaderMargins = 'mt-0';

  @property({ converter: booleanConverter })
  paylater = false;

  @property()
  clickEventMessage = 'PayPal Checkout Start';

  /**
   * Billing address returned by Paypal
   */
  @state()
  private internalBillingAddress: RmsBookstoreAddress | undefined;

  /**
   * Shipping address returned by Paypal
   */
  @state()
  private internalShippingAddress: RmsBookstoreAddress | undefined;

  /**
   * Paypal payment details
   */
  @state()
  private paymentDetails: PaymentDetails | undefined;

  @state()
  private isLoading = true;

  @state()
  private isEligible = true;

  private shippingConfig = {};
  private intent: 'capture' | 'authorize' = 'capture';
  private paypalButtonCreated = false;

  stateChanged(state: StoreState) {
    if (getLoadPaypalSdkStatus(state) === ActionStatus.succeeded && !this.paypalButtonCreated) {
      this.paypalButtonCreated = true;
      this.configureShippingConfig();
      this.createPaypalButton();
    } else if (getLoadPaypalSdkStatus(state) === ActionStatus.failed) {
      this.isLoading = false;
    }
  }

  protected updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    const oldShippableValue = _changedProperties.get('shippable');
    if (this.shippable !== oldShippableValue) {
      this.configureShippingConfig();
    }
  }

  renderComp() {
    return html`
      ${this.isLoading
        ? html`
          ${loading({
            columns: 1,
            width: '100%',
            height: this.buttonHeightPx ? `${this.buttonHeightPx}px` : '2rem',
            m: this.loaderMargins
          })}`
        : nothing
      }
      <div id="paypal-button" class="d-none"></div>
    `;
  }

  /**
   * Wait for the paypal button place order to render before adding the paypal button to it.
   * @param _changedProperties
   */
  async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    await this.setupPaypalButton();
  }

  async setupPaypalButton() {
    this.intent = this.shippable ? 'authorize' : 'capture';
    await store.dispatch(
      loadPaypalSdk({
        clientToken: this.clientToken,
        userAutoLogin: this.userAutoLogin,
        'enable-funding': 'paylater',
        currencyCode: this.currency,
        intent: this.intent,
      })
    );
  }

  configureShippingConfig() {
    if (this.shippable) {
      if (!this.usePayPalShippingAddress && this.shippingAddress) {
        this.shippingConfig = {
          enableShippingAddress: true,
          shippingAddressEditable: false,
          shippingAddressOverride: {
            recipientName: `${this.shippingAddress.firstName} ${this.shippingAddress.lastName}`,
            line1: `${this.shippingAddress.address1}`,
            line2: `${this.shippingAddress.address2 ? this.shippingAddress.address2 : ''}`,
            city: `${this.shippingAddress.city}`,
            countryCode: `${this.shippingAddress.country}`,
            postalCode: `${this.shippingAddress.zipCode}`,
            state: `${this.shippingAddress.state}`,
            phone: `${this.shippingAddress.phoneNumber}`,
          },
        };
      } else if (this.usePayPalShippingAddress) {
        this.shippingConfig = {
          enableShippingAddress: true,
          shippingAddressEditable: true,
        };
      }
    } else {
      this.shippingConfig = {};
    }
  }

  async createPaypalButton() {
    const paypalCheckoutInstance = getPaypalCheckoutInstance(store.getState() as StoreState);
    await waitForElementsToLoad(this.shadowRoot!, ['#paypal-button']);

    const button = window.paypal.Buttons({
      style: this.getPayPalButtonStyle(),
      fundingSource: this.paylater ? window.paypal.FUNDING.PAYLATER : window.paypal.FUNDING.PAYPAL,
      [getPaypalButtonName()]: async () => {
        const state = store.getState() as StoreState;
        emit({
          element: this,
          name: `mhecomm-paypal-checkout-start`,
          detail: {
            label: this.clickEventMessage,
            url: window.location.href,
            user: getUserDataForUserAnalytics(state),
            orderTotal: this.amount,
            ...getCartAnalyticsOverrides(state),
          },
        });
        await waitForCart();
        return hasOnlyFreeTrialProducts(getProductsFromCart(state))
          ? createBillingAgreement(this, paypalCheckoutInstance)
          : createOrder(this, paypalCheckoutInstance);
      },
      onApprove: (_data: PayPalCheckoutTokenizationOptions, _actions: any) => {
        if (this.usePayPalShippingAddress) {
          store.dispatch(updateActiveStep(undefined));
        }
        return paypalCheckoutInstance.tokenizePayment(_data).then(async (payload) => {
          const paypalBillingAddress: any = payload.details.billingAddress;
          if (Object.keys(paypalBillingAddress).length > 0) {
            this.internalBillingAddress = {
              firstName: payload.details.firstName,
              lastName: payload.details.lastName,
              phoneNumber: payload.details?.phone ?? '',
              country: paypalBillingAddress.countryCode,
              address1: paypalBillingAddress.line1,
              address2: paypalBillingAddress.line2,
              city: paypalBillingAddress.city,
              state: paypalBillingAddress.state,
              zipCode: paypalBillingAddress.postalCode,
            };
          } else {
            this.internalBillingAddress = undefined;
            console.error('PayPal returned no billing address!');
          }
          await store.dispatch(
            createDataCollector({
              clientToken: this.clientToken,
            })
          );
          this.paymentDetails = {
            paymentType: PaymentType.BRAINTREE_PAYPAL,
            paymentNonce: payload.nonce,
            deviceData: getDataCollector(store.getState() as StoreState).deviceData,
            paypalEmail: payload.details.email,
          };
          const paypalShippingAddress: any = payload.details.shippingAddress;
          if (paypalShippingAddress && this.usePayPalShippingAddress) {
            if (Object.keys(paypalShippingAddress).length > 0) {
              this.internalShippingAddress = {
                firstName: payload.details.firstName,
                lastName: payload.details.lastName,
                phoneNumber: payload.details?.phone ?? '',
                country: paypalShippingAddress.countryCode,
                address1: paypalShippingAddress.line1,
                address2: paypalShippingAddress.line2,
                city: paypalShippingAddress.city,
                state: paypalShippingAddress.state,
                zipCode: paypalShippingAddress.postalCode,
              };
            } else {
              this.internalShippingAddress = undefined;
              console.error('PayPal returned no shipping address!');
            }
          }

          emit({
            element: this,
            name: `mhecomm-paypal-checkout-info-collected`,
            detail: {
              billingAddress: this.internalBillingAddress,
              paymentDetails: this.paymentDetails,
              shippingAddress: this.internalShippingAddress,
            },
            composed: true,
          });
        });
      },
      onCancel: (data: any) => {
        console.log('PayPal payment cancelled', JSON.stringify(data));
      },
      onError: (err: any) => {
        console.error('PayPal error', err);
      },
    });
    this.isEligible = button.isEligible();
    if (!this.isEligible) {
      // Skip rendering if not eligible
      this.isLoading = false;
      return;
    }

    button.render(this.payPalButtonSelector()).then(() => {
      this.payPalButtonSelector().classList.remove('d-none');
      this.isLoading = false;
    });
  }

  /**
   * Gets the style for the PayPal button.
   *
   * @private
   */
  private getPayPalButtonStyle() {
    const style = {
      layout: this.buttonLayout,
      color: this.buttonColor,
      shape: this.buttonShape,
      label: this.buttonLabel,
    };

    if (this.buttonHeightPx) {
      style['height'] = this.buttonHeightPx;
    }

    return style;
  }

  private payPalButtonSelector() {
    return this.shadowRoot!.querySelector('#paypal-button') as HTMLElement;
  }
}

function getPaypalButtonName() {
  if (hasOnlyFreeTrialProducts(getProductsFromCart(store.getState() as StoreState))) {
    return CREATE_BILLING_AGREEMENT_CALLBACK_FN;
  }

  return CREATE_ORDER_CALLBACK_FN;
}
function createOrder(element, paypalCheckoutInstance) {
  return paypalCheckoutInstance.createPayment({
    flow: 'checkout',
    amount: element.amount,
    currency: element.currency,
    requestBillingAgreement: true,
    billingAgreementDescription: element.billingAgreementDescription,
    intent: element.intent,
    ...element.shippingConfig,
  });
}

function createBillingAgreement(element, paypalCheckoutInstance) {
  return paypalCheckoutInstance.createPayment({
    flow: 'vault',
    requestBillingAgreement: true,
    billingAgreementDescription: element.billingAgreementDescription,
    ...element.shippingConfig,
  });
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-paypal-button': PaypalButton;
  }
  interface Window {
    paypal: any;
  }
}
